/**
 * Compact summary bar: RAG proportion bar + verdict text + RAG counts.
 * Appears when the Summary toggle is set to "Compact bar".
 *
 * `insufficient` (the umbrella count for insufficient_data / not_available /
 * not_tracked) renders in the same neutral grey as `na` — distinct from RAG,
 * per the reliability design spec's neutral-state requirement.
 */
import { Box, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { ScorecardSummary } from '@/types/scorecard';

interface HealthBarProps {
  summary: ScorecardSummary;
}

const VERDICT_DOT: Record<string, string> = {
  green: '#6BFFAB',
  amber: '#FFB000',
  red: '#ef4444',
  na: '#6c6c78',
};

const NEUTRAL_COLOR = '#8a8a94';

function dominantStatus(s: ScorecardSummary): string {
  if (s.red > 0) return 'red';
  if (s.amber > 0) return 'amber';
  return 'green';
}

export function HealthBar({ summary }: HealthBarProps): ReactElement {
  const total = summary.total || 1;
  const dom = dominantStatus(summary);
  const dotColor = VERDICT_DOT[dom] ?? '#6c6c78';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        px: 2.25,
        py: 1.75,
        mb: 2.25,
      }}
      role="status"
      aria-label={`Health summary: ${summary.verdict}`}
    >
      {/* Verdict label */}
      <Stack direction="row" alignItems="center" spacing={1.125} sx={{ whiteSpace: 'nowrap' }}>
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: '50%',
            background: dotColor,
            boxShadow: `0 0 6px ${dotColor}70`,
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {summary.verdict}
        </Typography>
      </Stack>

      {/* RAG proportion bar */}
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          minWidth: 120,
        }}
        role="img"
        aria-label={`${summary.green} green, ${summary.amber} amber, ${summary.red} red, ${summary.insufficient} insufficient, ${summary.na} n/a`}
      >
        {summary.green > 0 && (
          <Box
            sx={{ flex: summary.green / total, background: '#6BFFAB', height: '100%' }}
          />
        )}
        {summary.amber > 0 && (
          <Box
            sx={{ flex: summary.amber / total, background: '#FFB000', height: '100%' }}
          />
        )}
        {summary.red > 0 && (
          <Box
            sx={{ flex: summary.red / total, background: '#ef4444', height: '100%' }}
          />
        )}
        {summary.insufficient > 0 && (
          <Box
            sx={{ flex: summary.insufficient / total, background: NEUTRAL_COLOR, height: '100%' }}
          />
        )}
        {summary.na > 0 && (
          <Box
            sx={{ flex: summary.na / total, background: '#6c6c78', height: '100%' }}
          />
        )}
      </Box>

      {/* Counts */}
      <Stack direction="row" spacing={1.75} sx={{ whiteSpace: 'nowrap' }}>
        {[
          { label: 'green', count: summary.green, color: '#6BFFAB' },
          { label: 'amber', count: summary.amber, color: '#FFB000' },
          { label: 'red', count: summary.red, color: '#ef4444' },
          { label: 'not yet reliable', count: summary.insufficient, color: NEUTRAL_COLOR },
          { label: 'n/a', count: summary.na, color: '#6c6c78' },
        ].map(({ label, count, color }) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
              }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6875rem',
                color: 'text.secondary',
              }}
            >
              {count}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
